import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Wifi } from 'lucide-react';

const ServerStatus = () => {
    const [serverData, setServerData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    // ===== CONFIGURATION FAKE PLAYERS =====
    const FAKE_PLAYER_OFFSET = 30; // Mets à 0 pour utiliser le système réel uniquement
    const FAKE_MIN = 10; // Minimum de joueurs fake
    const FAKE_MAX = 100; // Maximum de joueurs fake

    const SERVER_IP = '54.36.175.204';
    const SERVER_PORT = '25565';

    // Fonction pour calculer le coefficient selon l'heure (0 à 1)
    const getHourCoefficient = () => {
        const now = new Date();
        const hour = now.getHours();
        const minute = now.getMinutes();
        const timeDecimal = hour + minute / 60;

        // 0h-6h : très faible (0.1-0.2)
        if (timeDecimal >= 0 && timeDecimal < 6) {
            return 0.1 + (timeDecimal / 6) * 0.1;
        }
        // 6h-12h : augmentation progressive (0.2-0.5)
        else if (timeDecimal >= 6 && timeDecimal < 12) {
            return 0.2 + ((timeDecimal - 6) / 6) * 0.3;
        }
        // 12h-14h : pic midi (0.5-0.7)
        else if (timeDecimal >= 12 && timeDecimal < 14) {
            return 0.5 + ((timeDecimal - 12) / 2) * 0.2;
        }
        // 14h-17h : après-midi (0.6-0.5)
        else if (timeDecimal >= 14 && timeDecimal < 17) {
            return 0.6 - ((timeDecimal - 14) / 3) * 0.1;
        }
        // 17h-21h : montée vers le pic (0.5-0.9)
        else if (timeDecimal >= 17 && timeDecimal < 21) {
            return 0.5 + ((timeDecimal - 17) / 4) * 0.4;
        }
        // 21h-00h : PIC MAXIMUM (0.9-1.0)
        else if (timeDecimal >= 21 && timeDecimal < 24) {
            return 0.9 + ((timeDecimal - 21) / 3) * 0.1;
        }

        return 0.5;
    };

    // Récupérer ou initialiser les fake players depuis localStorage
    const getFakePlayers = () => {
        if (FAKE_PLAYER_OFFSET === 0) return null;

        const stored = localStorage.getItem('pokemovie_fake_players');
        if (stored) {
            const data = JSON.parse(stored);
            // Vérifier que la donnée n'est pas trop vieille (> 5 minutes)
            if (Date.now() - data.timestamp < 5 * 60 * 1000) {
                return data.count;
            }
        }

        // Initialiser avec un nombre basé sur l'heure
        const hourCoef = getHourCoefficient();
        const initial = Math.floor(FAKE_MIN + (FAKE_MAX - FAKE_MIN) * hourCoef);
        setFakePlayers(initial);
        return initial;
    };

    // Sauvegarder les fake players dans localStorage
    const setFakePlayers = (count) => {
        if (FAKE_PLAYER_OFFSET === 0) return;

        localStorage.setItem('pokemovie_fake_players', JSON.stringify({
            count: count,
            timestamp: Date.now()
        }));

        // Déclencher un event pour synchroniser les autres onglets
        window.dispatchEvent(new Event('pokemovie_players_update'));
    };

    // Simuler des variations réalistes
    const simulateFakePlayerChange = () => {
        if (FAKE_PLAYER_OFFSET === 0) return;

        const current = getFakePlayers();
        if (current === null) return;

        const hourCoef = getHourCoefficient();
        const target = Math.floor(FAKE_MIN + (FAKE_MAX - FAKE_MIN) * hourCoef);
        const diff = target - current;

        let change;
        if (Math.abs(diff) > 10) {
            change = Math.sign(diff) * (Math.floor(Math.random() * 3) + 1);
        } else if (Math.abs(diff) > 5) {
            change = Math.sign(diff) * (Math.floor(Math.random() * 2) + 1);
        } else {
            change = Math.floor(Math.random() * 5) - 2;
        }

        let newCount = current + change;
        newCount = Math.max(Math.floor(FAKE_MIN * 0.3), newCount);
        newCount = Math.min(FAKE_MAX, newCount);

        setFakePlayers(newCount);

        // Debug
        //console.log(`[FakePlayers] ${new Date().getHours()}h - Players: ${newCount} - Target: ${target} - Coef: ${(hourCoef * 100).toFixed(0)}%`);
    };

    const fetchServerStatus = async () => {
        try {
            const response = await fetch(`https://api.mcsrvstat.us/3/${SERVER_IP}:${SERVER_PORT}`);
            const data = await response.json();

            if (data.online) {
                const realPlayers = data.players?.online || 0;
                const maxPlayers = data.players?.max || 1000;

                let displayPlayers;
                if (FAKE_PLAYER_OFFSET === 0) {
                    // Mode réel uniquement
                    displayPlayers = realPlayers;
                } else {
                    // Mode fake : on utilise le nombre stocké en localStorage
                    const fakeCount = getFakePlayers();
                    displayPlayers = fakeCount !== null ? fakeCount : realPlayers;
                }

                setServerData({
                    online: true,
                    players: displayPlayers,
                    maxPlayers: maxPlayers,
                    version: data.version || 'Unknown',
                    motd: data.motd?.clean?.[0] || 'PokeMovie - Serveur Cobblemon',
                    ping: Math.floor(Math.random() * 50) + 20
                });
                setError(false);
            } else {
                setServerData({ online: false });
                setError(false);
            }
            setLoading(false);
        } catch (err) {
            console.error('Error fetching server status:', err);
            setError(true);
            setLoading(false);
        }
    };

    // Écouter les changements de localStorage (synchronisation entre onglets)
    useEffect(() => {
        if (FAKE_PLAYER_OFFSET === 0) return;

        const handleStorageChange = () => {
            // Re-fetch pour mettre à jour l'affichage
            fetchServerStatus();
        };

        window.addEventListener('pokemovie_players_update', handleStorageChange);
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('pokemovie_players_update', handleStorageChange);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [FAKE_PLAYER_OFFSET]);

    // Système de fake players avec intervalle variable
    useEffect(() => {
        if (FAKE_PLAYER_OFFSET === 0) return;

        const scheduleNextUpdate = () => {
            const delay = Math.floor(Math.random() * 40000) + 20000; // 20-60 secondes

            return setTimeout(() => {
                simulateFakePlayerChange();
                fetchServerStatus(); // Rafraîchir l'affichage
                nextUpdate = scheduleNextUpdate();
            }, delay);
        };

        // Premier update après 5 secondes
        const initialTimeout = setTimeout(() => {
            simulateFakePlayerChange();
            fetchServerStatus();
        }, 5000);

        let nextUpdate = scheduleNextUpdate();

        return () => {
            clearTimeout(initialTimeout);
            if (nextUpdate) clearTimeout(nextUpdate);
        };
    }, [FAKE_PLAYER_OFFSET]);

    // Fetch initial et refresh périodique du statut réel
    useEffect(() => {
        fetchServerStatus();
        const interval = setInterval(fetchServerStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    if (loading) {
        return (
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-gray-100 border-2 border-gray-200">
                <div className="w-3 h-3 rounded-full bg-yellow-400 animate-pulse"></div>
                <span className="font-bold text-gray-600">Connexion...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-red-50 border-2 border-red-200">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="font-bold text-red-600">Erreur de connexion</span>
            </div>
        );
    }

    if (!serverData?.online) {
        return (
            <motion.div
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-red-50 border-2 border-red-200"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring" }}
            >
                <motion.div
                    className="w-3 h-3 rounded-full bg-red-500"
                    animate={{ opacity: [1, 0.3, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                ></motion.div>
                <span className="font-bold text-red-600">Serveur Hors Ligne</span>
            </motion.div>
        );
    }

    return (
        <motion.div
            className="inline-flex items-center gap-4 px-6 py-3 rounded-full bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 shadow-lg"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 200 }}
        >
            {/* Status Indicator */}
            <motion.div
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
            >
                <motion.div
                    className="w-3 h-3 rounded-full bg-green-500 shadow-lg"
                    animate={{
                        boxShadow: [
                            '0 0 0 0 rgba(34, 197, 94, 0.7)',
                            '0 0 0 8px rgba(34, 197, 94, 0)',
                        ]
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                ></motion.div>
                <span className="font-black text-green-600 text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    EN LIGNE
                </span>
            </motion.div>

            {/* Divider */}
            <div className="w-px h-6 bg-green-200"></div>

            {/* Players Count */}
            <motion.div
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
            >
                <Users className="w-4 h-4 text-green-600" />
                <span className="font-black text-green-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {serverData.players}/{serverData.maxPlayers}
                </span>
            </motion.div>

            {/* Ping */}
            <motion.div
                className="flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
            >
                <Wifi className="w-4 h-4 text-green-600" />
                <span className="font-bold text-green-600 text-sm">
                    {serverData.ping}ms
                </span>
            </motion.div>
        </motion.div>
    );
};

export default ServerStatus;